import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, OfflineState, useToast } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { PageHeader } from "../components/PageHeader";
import { BasketBuilder, type BasketLine } from "../components/BasketBuilder";
import { ForbiddenInline } from "../components/ForbiddenInline";
import { useCreateDemande } from "../lib/api";
import { CAPS } from "../lib/caps";
import type { Id, Priorite } from "../lib/types";

/**
 * DemandeNewPage — build + create a demande for a known dossier/patient (passed via query params from
 * the accueil/intake flow). Assembles the basket (manual ids or scan-seeded), then POST /demandes.
 * On success → the demande workspace (billing + tubes). 5 states: forbidden, offline, plus the
 * mutation's loading/error surfaced via toast + button state.
 */
export function DemandeNewPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canCreate = useCan(CAPS.demandeCreate);

  const [params] = useSearchParams();
  const dossierId = params.get("dossier");
  const patientId = params.get("patient");
  const nom = params.get("nom") ?? "";

  const [lines, setLines] = useState<BasketLine[]>([]);
  const [priorite, setPriorite] = useState<Priorite>("routine");
  const create = useCreateDemande();

  if (!canCreate) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
        <PageHeader title={t("reception.demande.title")} />
        <ForbiddenInline requiredCapabilities={[CAPS.demandeCreate]} />
      </div>
    );
  }

  // Missing the dossier/patient context — send the operator back to pick a patient.
  if (!dossierId || !patientId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
        <PageHeader
          title={t("reception.demande.title")}
          actions={
            <Button variant="ghost" onClick={() => navigate("/reception")}>
              {t("reception.back")}
            </Button>
          }
        />
        <p style={{ color: token.slate }}>{t("reception.search.emptyDesc")}</p>
      </div>
    );
  }

  const addLine = (line: BasketLine) => setLines((ls) => [...ls, line]);
  const removeLine = (id: Id) =>
    setLines((ls) => ls.filter((l) => String(l.parametre_labo_id) !== String(id)));
  const seedMany = (incoming: BasketLine[]) =>
    setLines((ls) => {
      const have = new Set(ls.map((l) => String(l.parametre_labo_id)));
      return [...ls, ...incoming.filter((l) => !have.has(String(l.parametre_labo_id)))];
    });

  const submit = () => {
    create.mutate(
      {
        dossier_patient_id: dossierId,
        patient_id: patientId,
        source: "manuel",
        priorite,
        parametre_labo_ids: lines.map((l) => l.parametre_labo_id),
      },
      {
        onSuccess: (demande) => {
          toast.success(t("reception.toast.demandeCreated", { ref: demande.reference }));
          navigate(`/reception/demandes/${demande.id}`);
        },
        onError: (e) => toast.error(e.message || t("reception.toast.demandeError")),
      },
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <PageHeader
        title={t("reception.demande.title")}
        subtitle={nom}
        actions={
          <Button variant="ghost" onClick={() => navigate("/reception")}>
            {t("reception.back")}
          </Button>
        }
      />

      {!online ? (
        <OfflineState />
      ) : (
        <BasketBuilder
          lines={lines}
          priorite={priorite}
          onPrioriteChange={setPriorite}
          onAdd={addLine}
          onRemove={removeLine}
          onSeedMany={seedMany}
          creating={create.isPending}
          canCreate={canCreate}
          onCreate={submit}
        />
      )}
    </div>
  );
}
